/**
 * @voltxampere/shared — Engine error leaf classes
 *
 * Covers SPECS §5.2 rows 7–12 (the `E_SIM_*` family). Severities and
 * recoverability pulled verbatim from the table: `E_SIM_TIMEOUT` is
 * a `warning`, `E_SIM_CANCELLED` is `info`, and both
 * `E_SIM_INVALID_MODEL` + `E_SIM_UNSUPPORTED_ANALYSIS` are
 * non-recoverable (the circuit/model itself needs to change).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { EngineError } from "./categories.js";
export class SimConvergenceError extends EngineError {
    code = "E_SIM_CONVERGENCE";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
export class SimSingularMatrixError extends EngineError {
    code = "E_SIM_SINGULAR";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
export class SimTimeoutError extends EngineError {
    code = "E_SIM_TIMEOUT";
    recoverable = true;
    defaultSeverity() {
        return "warning";
    }
}
export class SimInvalidModelError extends EngineError {
    code = "E_SIM_INVALID_MODEL";
    recoverable = false;
    defaultSeverity() {
        return "error";
    }
}
export class SimUnsupportedAnalysisError extends EngineError {
    code = "E_SIM_UNSUPPORTED_ANALYSIS";
    recoverable = false;
    defaultSeverity() {
        return "error";
    }
}
export class SimCancelledError extends EngineError {
    code = "E_SIM_CANCELLED";
    recoverable = true;
    defaultSeverity() {
        return "info";
    }
}
/**
 * Sprint 5 raises this when a {@link DCStampFn} for a registered
 * component type has not been implemented yet (BJT, MOSFET, op-amp
 * are the Sprint 5 offenders — Sprint 7+ will land them). The class
 * reuses `E_SIM_UNSUPPORTED_ANALYSIS` because SPECS §5.2 is a closed
 * 27-code catalog and "stamp for this component is not implemented"
 * is the same shape of failure as "this analysis kind is not
 * implemented" — both are "engine cannot execute this request, fix
 * the model or upgrade".
 *
 * Distinct from {@link SimUnsupportedAnalysisError} so callers can
 * narrow with `instanceof` and route the recovery UX (e.g. "swap to
 * a simpler component" vs "switch to a supported analysis").
 */
export class StampNotImplementedError extends EngineError {
    code = "E_SIM_UNSUPPORTED_ANALYSIS";
    recoverable = false;
    defaultSeverity() {
        return "error";
    }
}
/**
 * Sprint 6 raises this when the transient adaptive-step controller
 * (SPECS §3.9) cannot find a step size large enough to make
 * forward progress without exceeding the LTE budget — i.e. a step
 * was rejected, halved, rejected again, halved again, and so on
 * until `h < h_min = 1e-18`. The integrator gives up at that point
 * because no useful work can happen at sub-attosecond resolution.
 *
 * The class reuses `E_SIM_CONVERGENCE` because SPECS §5.2 is a
 * closed 27-code catalog and "transient integrator could not
 * advance" is the time-domain analogue of "Newton-Raphson could
 * not converge" — both are recoverable engine errors signalling
 * "the solver gave up; relax tolerances or change the circuit".
 *
 * Distinct class (rather than a {@link SimConvergenceError}
 * subclass) so callers can `instanceof` filter the transient case
 * separately from the per-step Newton convergence case — the UX
 * recovery hint is different ("relax reltol/abstol or shorten
 * tstop" vs "try a different start point or add gmin").
 *
 * Recommended `meta` shape: `{ t, h, hMin, attempts, lastLte }`.
 */
export class TransientStepFailedError extends EngineError {
    code = "E_SIM_CONVERGENCE";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
/**
 * Sprint 55 — ngspice parity drift error. Raised by the parity
 * harness when the vxa-engine simulation result drifts beyond the
 * corpus-defined tolerance band against the ngspice reference.
 *
 * Recoverable in the sense that the user can choose to widen the
 * tolerance band in the corpus meta.json OR investigate the
 * underlying stamp / numerical issue. NOT silently auto-recovered.
 *
 * Recommended `meta` shape:
 *
 *   {
 *     corpusId: string;          // e.g. "fathers-circuit"
 *     analysisType: "dc" | "transient" | "ac";
 *     watchNode: string;         // net id whose value drifted
 *     engineValue: number;       // observed vxa-engine reading
 *     referenceValue: number;    // ngspice ground truth
 *     toleranceV?: number;       // absolute tolerance (volts)
 *     tolerancePct?: number;     // relative tolerance (percent)
 *   }
 *
 * UX recovery hint (Recovery Center suggestion): "Engine drift X V
 * vs ngspice reference Y V at node Z. Tolerance Y%/Z V. Check stamp
 * model or widen corpus tolerance after manual verification."
 */
export class ParityToleranceError extends EngineError {
    code = "E_SIM_PARITY_DRIFT";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
//# sourceMappingURL=engine-errors.js.map